import { RondChargement } from '@/components/noyaux/noyau-en-cours';
import { NoyauxIndisponiblesError, SessionExpireeError, getApi } from '@/lib/api';
import { effacerSession, lireSession } from '@/lib/session';
import { ActionBar, Alert, Button, Dialog } from '@pms-digital/design-system';
import { NOYAU_CODES, type NoyauCode } from '@rosetta/shared';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useId, useRef, useState } from 'react';
import { LigneNoyauAjout, estPris } from './ligne-noyau-ajout';
import { useNoyauxTexte } from './use-noyaux-texte';

interface ChoixNoyauxAjoutProps {
  /** The finished translation of the card: its text is the one translated again. */
  id: string;
  titre?: string | undefined;
  onFermer: () => void;
  /** The new translation is launched: its identifier, and the noyaux it is for. */
  onLance: (ajout: string, noyaux: readonly NoyauCode[]) => void;
}

/** Under the noyaux while none is checked, as on the input screen. */
export const AIDE_AUCUN_NOYAU = 'Veuillez choisir au moins un noyau cible';
export const MESSAGE_LANCEMENT_ECHEC = "La traduction n'a pas pu être lancée, réessayez.";

/** One noyau, or several, taken since the choice was read (another tab, another device). */
export function messageIndisponibles(noyaux: readonly string[]): string {
  return noyaux.length === 1
    ? `${noyaux[0]} vient d'être traduit ou est en cours : le choix est mis à jour.`
    : `${noyaux.join(', ')} viennent d'être traduits ou sont en cours : le choix est mis à jour.`;
}

/**
 * The choice of the noyaux to add to a text (#11, N1, and N2 while one of them is being
 * translated), opened by "autre noyau" on a card of the history: a modal over the history, the
 * six noyaux with what became of each one for this text, then "annuler" and "traduire".
 *
 * Only a click on "traduire" writes in Tahiti (a paid treatment): a new translation of the same
 * document, towards the checked noyaux only. "traduire" stays inactive (`aria-disabled`, described
 * by the help sentence) while nothing is checked, and a second press while the first one is on
 * its way does nothing. A noyau translated or being translated cannot be checked; the client reads
 * the noyaux again before writing, so a choice that went out of date launches nothing and says so.
 */
export function ChoixNoyauxAjout({ id, titre, onFermer, onLance }: ChoixNoyauxAjoutProps) {
  const titreId = useId();
  const aideId = useId();
  const corpsRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const lecture = useNoyauxTexte(id);
  const [coches, setCoches] = useState<ReadonlySet<NoyauCode>>(() => new Set());
  const [envoi, setEnvoi] = useState(false);
  const enVol = useRef(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const noyaux = lecture.data;
  // A noyau checked, then taken meanwhile (the reading every 5 s), is no longer part of the choice.
  const choisis = NOYAU_CODES.filter(
    (noyau) =>
      coches.has(noyau) && !noyaux?.some((ligne) => ligne.noyau === noyau && estPris(ligne.etat)),
  );
  const aucun = choisis.length === 0;

  // Once the noyaux are there, the keyboard goes to the first one that can be checked (N1), unless
  // the user has already moved it: until then it is where the dialog put it, on its close button
  // (or on the panel), its name having been read.
  useEffect(() => {
    const panneau = corpsRef.current?.closest<HTMLElement>('[role="dialog"]');
    if (noyaux === undefined || panneau === null || panneau === undefined) return;
    const actif = document.activeElement;
    if (actif !== panneau && actif !== panneau.querySelector('button[aria-label="fermer"]')) return;
    panneau.querySelector<HTMLElement>('[role="checkbox"]:not([aria-disabled="true"])')?.focus();
  }, [noyaux]);

  useEffect(() => {
    if (!(lecture.error instanceof SessionExpireeError)) return;
    effacerSession();
    void navigate({ to: '/login' });
  }, [lecture.error, navigate]);

  function basculer(noyau: NoyauCode) {
    setErreur(null);
    setCoches((courants) => {
      const suivants = new Set(courants);
      if (!suivants.delete(noyau)) suivants.add(noyau);
      return suivants;
    });
  }

  async function traduire() {
    if (aucun || enVol.current) return;
    const session = lireSession();
    if (session === null) {
      void navigate({ to: '/login' });
      return;
    }
    enVol.current = true;
    setEnvoi(true);
    setErreur(null);
    try {
      const ajout = await getApi().ajouterNoyaux(session, id, { noyaux: choisis });
      onLance(ajout, choisis);
    } catch (probleme) {
      if (probleme instanceof SessionExpireeError) {
        effacerSession();
        void navigate({ to: '/login' });
      } else if (probleme instanceof NoyauxIndisponiblesError) {
        setErreur(messageIndisponibles(probleme.noyaux));
        void lecture.refetch();
      } else {
        setErreur(MESSAGE_LANCEMENT_ECHEC);
      }
    } finally {
      enVol.current = false;
      setEnvoi(false);
    }
  }

  function corps() {
    if (noyaux !== undefined) {
      return (
        <ul aria-labelledby={titreId} className="mt-4 border-gray-100 border-t">
          {noyaux.map(({ noyau, etat }) => (
            <LigneNoyauAjout
              key={noyau}
              noyau={noyau}
              etat={etat}
              coche={choisis.includes(noyau)}
              onBasculer={() => basculer(noyau)}
              idResultat={id}
            />
          ))}
        </ul>
      );
    }
    if (lecture.error !== null && !(lecture.error instanceof SessionExpireeError)) {
      return (
        <div className="mt-4">
          <Alert
            tone="danger"
            action={
              <Button variant="primary" onClick={() => void lecture.refetch()}>
                réessayer
              </Button>
            }
          >
            Les noyaux de ce texte n'ont pas pu être lus.
          </Alert>
        </div>
      );
    }
    return (
      <output className="mt-4 flex items-center gap-2 border-gray-100 border-y py-4 text-body text-gray-700">
        <RondChargement />
        Lecture des noyaux...
      </output>
    );
  }

  return (
    <Dialog
      open
      onOpenChange={(ouvert) => {
        if (!ouvert) onFermer();
      }}
      size="form"
      title={<span id={titreId}>Traduire vers un autre noyau</span>}
      description="Le texte ne change pas : cochez les noyaux à ajouter."
      closeLabel="fermer"
      actions={
        <>
          {aucun && noyaux !== undefined ? (
            <p id={aideId} className="mb-4 text-gray-600 text-small">
              {AIDE_AUCUN_NOYAU}
            </p>
          ) : null}
          <ActionBar
            secondary={
              <Button variant="ghost" onClick={onFermer}>
                annuler
              </Button>
            }
            primary={
              <Button
                variant="secondary"
                aria-disabled={aucun || envoi}
                aria-describedby={aucun && noyaux !== undefined ? aideId : undefined}
                onClick={() => void traduire()}
              >
                {envoi ? 'traduction...' : 'traduire'}
              </Button>
            }
          />
        </>
      }
    >
      <div ref={corpsRef}>
        <p
          className={`font-semibold text-body leading-[1.4] ${titre === undefined ? 'text-gray-600 italic' : ''}`}
        >
          {titre ?? 'Sans titre'}
        </p>
        {corps()}
        {erreur === null ? null : (
          <div className="mt-4">
            <Alert tone="danger">{erreur}</Alert>
          </div>
        )}
      </div>
    </Dialog>
  );
}
